import { useEffect, useRef } from 'react'
import type { RealtimeEvent } from '@sac/contracts'

/**
 * One EventSource per stream URL, reconnecting on its own (native EventSource behavior).
 * `onEvent` is re-subscribed via a ref so the connection itself doesn't reset on every render.
 */
export function useRealtimeStream(url: string | null, onEvent: (event: RealtimeEvent) => void) {
  const handlerRef = useRef(onEvent)
  handlerRef.current = onEvent

  useEffect(() => {
    if (!url) return
    const source = new EventSource(url, { withCredentials: true })
    const listener = (msg: MessageEvent) => {
      try {
        handlerRef.current(JSON.parse(msg.data) as RealtimeEvent)
      } catch {
        // ignore malformed event
      }
    }
    // The server sets `event: <type>` per message; a generic listener on every known type
    // keeps this simple without re-registering per event name.
    const types = [
      'message.created',
      'message.status_changed',
      'conversation.created',
      'conversation.updated',
      'conversation.assigned',
      'conversation.status_changed',
      'conversation.reopened',
      'conversation.unread_changed',
      'note.created',
      'task.created',
      'task.completed',
      'identity.linked',
    ]
    for (const t of types) source.addEventListener(t, listener)

    return () => {
      for (const t of types) source.removeEventListener(t, listener)
      source.close()
    }
  }, [url])
}
